import { useWorkspaceId } from "../../workspace/useWorkspaceId";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
    BASE_API_URL,
    HttpError,
} from "../../api/utils/useGovernmentAgencyHubAppQueryClient";
import { useHttpGovernmentAgencyHubAppHeaders } from "../../api/utils/useHttpGovernmentAgencyHubAppHeaders";

interface UploadTaxSubmissionPayload {
    deadline?: string;
    delegatedTaxId: string | null;
    fileContent: string;
    fileName: string;
    governmentAgency?: {
        id: string;
        sendDate: Date;
    };
    intermediaryTaxId: string;
    intermediarySiteCode?: string;
    isFullUpload: boolean;
    levy?: { levyId: string; accrualYear?: number };
    uploadFlow: "ADE" | "SDI" | "TS";
}

export const useUploadTaxSubmission = () => {
    const workspaceId = useWorkspaceId();
    const headers = useHttpGovernmentAgencyHubAppHeaders({ method: "POST" });
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    const mutation = useMutation({
        mutationFn: async (payload: UploadTaxSubmissionPayload) => {
            const response = await fetch(
                `${BASE_API_URL}/tenants/${workspaceId}/tax-submissions/upload`,
                {
                    headers,
                    method: "POST",
                    body: JSON.stringify({
                        deadline: payload.deadline,
                        delegatedTaxId: payload.delegatedTaxId,
                        fileContent: payload.fileContent,
                        fileName: payload.fileName,
                        ...(payload.governmentAgency && {
                            governmentAgency: {
                                id: payload.governmentAgency.id,
                                sendDate: payload.governmentAgency.sendDate,
                            },
                        }),
                        intermediaryTaxId: payload.intermediaryTaxId,
                        intermediarySiteCode: payload.intermediarySiteCode,
                        isFullUpload: payload.isFullUpload,
                        levy: payload.levy,
                        uploadFlow: payload.uploadFlow,
                    }),
                },
            );

            if (!response.ok) {
                throw new HttpError(response);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["useGetTaxSubmissions"],
            });
            queryClient.invalidateQueries({ queryKey: ["useGetKPIs"] });
            queryClient.invalidateQueries({ queryKey: ["useGetSubjects"] });
            queryClient.invalidateQueries({ queryKey: ["useGetTaxPayers"] });
            queryClient.invalidateQueries({
                queryKey: ["useGetExpiringIdentities"],
            });
        },
        meta: {
            errorMessage: t("errors.messages.uploadTaxSubmission"),
        },
    });

    return mutation;
};
